import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Layer, TemplateSettings, TemplateData } from '../../types/design';
import { TextStyle } from '../../pages/template-editor/textEditorTypes';
import { ImageSettings } from './ImagePropertiesPanel';
import { ShapeSettings } from './ShapePropertiesPanel';
import { Play, Pause, ZoomIn, ZoomOut, X, ChevronRight, Settings2, Clock } from 'lucide-react';
import { getEffectiveKeyframes, calculateMinTemplateDuration } from '../../utils/animationUtils';

interface TimelinePanelProps {
  isOpen: boolean;
  onClose: () => void;
  layers: Layer[];
  onUpdateLayer: (layerId: string, updates: Partial<Layer>) => void;
  templateSettings: TemplateSettings;
  onUpdateTemplateSettings: (settings: Partial<TemplateSettings>) => void;
  textStyles?: Record<string, Partial<TextStyle>>;
  imageSettings?: Record<string, ImageSettings>;
  shapeSettings?: Record<string, ShapeSettings>;
  selectedLayerIds: string[];
  onSelectLayer: (layerId: string) => void;
  currentTime: number;
  onTimeChange: (time: number) => void;
  isPlaying: boolean;
  onTogglePlay: () => void;
  videoSettings?: Record<string, any>;
  templateData?: TemplateData;
}

interface TimelineTrack {
  id: string;
  name: string;
  type: string;
  animation: NonNullable<TemplateSettings['animation']>;
  depth: number;
  isLayer: boolean;
}

export function TimelinePanel({
  isOpen,
  onClose,
  layers = [],
  onUpdateLayer,
  templateSettings,
  onUpdateTemplateSettings,
  textStyles,
  imageSettings,
  shapeSettings,
  selectedLayerIds = [],
  onSelectLayer,
  currentTime,
  onTimeChange,
  isPlaying,
  onTogglePlay,
  videoSettings,
  templateData
}: TimelinePanelProps) {
  const [zoom, setZoom] = useState(100); // pixels per second (100px = 1s)
  const [isAdvancedVisible, setIsAdvancedVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [draggingBar, setDraggingBar] = useState<{
    id: string,
    type: 'move' | 'resize-start' | 'resize-end',
    startX: number,
    originalDelay: number,
    originalDuration: number
  } | null>(null);

  const [panelHeight, setPanelHeight] = useState(250);
  const [isResizingPanel, setIsResizingPanel] = useState(false);
  const [resizeStartY, setResizeStartY] = useState(0);
  const [resizeStartHeight, setResizeStartHeight] = useState(0);

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizingPanel(true);
    setResizeStartY(e.clientY);
    setResizeStartHeight(panelHeight);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizingPanel) return;
      const deltaY = resizeStartY - e.clientY;
      const newHeight = Math.max(120, Math.min(window.innerHeight * 0.8, resizeStartHeight + deltaY));
      setPanelHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizingPanel(false);
    };

    if (isResizingPanel) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'row-resize';
    } else {
      document.body.style.cursor = '';
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
    };
  }, [isResizingPanel, resizeStartY, resizeStartHeight]);

  // Helper to flatten layer tree
  const flattenLayers = (node: Layer, depth = 0, list: { layer: Layer; depth: number }[] = []) => {
    list.push({ layer: node, depth });
    if (node.children) {
      node.children.forEach(child => flattenLayers(child, depth + 1, list));
    }
    return list;
  };

  const [expandedTracks, setExpandedTracks] = useState<Set<string>>(new Set());

  const toggleTrack = (id: string) => {
    const next = new Set(expandedTracks);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedTracks(next);
  };

  const tracks = useMemo<TimelineTrack[]>(() => {
    const list: TimelineTrack[] = [];

    // 1. Page / Template Animation
    if (templateSettings?.animation && templateSettings.animation.type !== 'none') {
      list.push({
        id: 'root',
        name: 'Page',
        type: 'Template',
        animation: templateSettings.animation,
        depth: 0,
        isLayer: false
      });
    }

    // 2. Layer Animations
    const flatLayers: { layer: Layer; depth: number }[] = [];
    layers.forEach(l => flattenLayers(l, 0, flatLayers));

    const seenIds = new Set<string>();

    flatLayers.forEach(({ layer, depth }) => {
      if (layer.id === 'root') return;
      if (seenIds.has(layer.id)) return;
      seenIds.add(layer.id);

      let animation = layer.animation;

      const tStyle = textStyles?.[layer.id];
      const iSetting = imageSettings?.[layer.id];
      const sSetting = shapeSettings?.[layer.id];
      const vSetting = videoSettings?.[layer.id];

      if (tStyle?.animation?.type && tStyle.animation.type !== 'none') {
        animation = tStyle.animation;
      } else if (iSetting?.animation?.type && iSetting.animation.type !== 'none') {
        animation = iSetting.animation;
      } else if (sSetting?.animation && sSetting.animation.type !== 'none') {
        animation = sSetting.animation;
      } else if (vSetting?.animation && vSetting.animation.type !== 'none') {
        animation = vSetting.animation;
      }

      if (animation && animation.type !== 'none') {
        let typeLabel = 'Element';
        if (layer.type === 'text') typeLabel = 'Text';
        else if (layer.type === 'image') typeLabel = 'Image';
        else if (['rectangle', 'shape', 'icon', 'line', 'arrow'].includes(layer.type)) typeLabel = (layer.type === 'line' || layer.type === 'arrow') ? 'Line' : 'Shape';
        else if (layer.type === 'frame') typeLabel = 'Frame';
        else if (layer.type === 'group') typeLabel = 'Group';

        list.push({
          id: layer.id,
          name: layer.name || layer.id,
          type: typeLabel,
          animation: {
            ...animation,
            keyframes: getEffectiveKeyframes(animation)
          },
          depth: depth,
          isLayer: true
        });
      }
    });

    return list;
  }, [layers, templateSettings, textStyles, imageSettings, shapeSettings, videoSettings]);

  const timeToPx = (time: number) => time * (zoom / 1000);
  const pxToTime = (px: number) => px / (zoom / 1000);

  const handleUpdate = (id: string, updates: any) => {
    if (id === 'root') {
      onUpdateTemplateSettings(updates);
    } else {
      onUpdateLayer(id, updates);
    }
  };

  const handleAddKeyframe = (track: TimelineTrack) => {
    const newKeyframe = {
      time: currentTime - (track.animation.delay || 0),
      properties: {} // User will fill this in
    };

    const existingKeyframes = track.animation.keyframes || [];
    const newKeyframes = [...existingKeyframes, newKeyframe].sort((a, b) => a.time - b.time);

    handleUpdate(track.id, {
      animation: {
        ...track.animation,
        type: track.animation.type === 'none' ? 'custom' : track.animation.type,
        keyframes: newKeyframes
      }
    });
  };

  const handleMouseDown = (e: React.MouseEvent, track: TimelineTrack, type: 'move' | 'resize-start' | 'resize-end') => {
    e.stopPropagation();
    e.preventDefault();
    setDraggingBar({
      id: track.id,
      type,
      startX: e.clientX,
      originalDelay: track.animation.delay || 0,
      originalDuration: track.animation.duration || 1000
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!draggingBar) return;

      const deltaPx = e.clientX - draggingBar.startX;
      const deltaTime = pxToTime(deltaPx);

      const track = tracks.find(t => t.id === draggingBar.id);
      if (!track) return;

      let newDelay = draggingBar.originalDelay;
      let newDuration = draggingBar.originalDuration;

      if (draggingBar.type === 'move') {
        newDelay = Math.max(0, draggingBar.originalDelay + deltaTime);
      } else if (draggingBar.type === 'resize-end') {
        newDuration = Math.max(100, draggingBar.originalDuration + deltaTime);
      } else if (draggingBar.type === 'resize-start') {
        const potentialDelay = draggingBar.originalDelay + deltaTime;
        if (potentialDelay >= 0 && draggingBar.originalDuration - deltaTime >= 100) {
          newDelay = potentialDelay;
          newDuration = draggingBar.originalDuration - deltaTime;
        }
      }

      newDelay = Math.round(newDelay / 50) * 50;
      newDuration = Math.round(newDuration / 50) * 50;

      console.log('[ANIM_DEBUG] Timeline Bar Adjusted:', { id: draggingBar.id, type: draggingBar.type, delay: newDelay, duration: newDuration });
      handleUpdate(draggingBar.id, {
        animation: {
          ...track.animation,
          delay: newDelay,
          duration: newDuration
        }
      });
    };

    const handleMouseUp = () => {
      setDraggingBar(null);
    };

    if (draggingBar) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingBar, tracks, zoom]);

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-[0_-20px_50px_rgba(0,0,0,0.05)] z-[60] flex flex-col font-sans select-none overflow-hidden rounded-t-[20px] ${isResizingPanel ? '' : 'transition-[height] duration-75'}`}
      style={{ height: `${panelHeight}px` }}
    >
      {/* Panel Resize Handle */}
      <div
        className="absolute top-0 left-0 right-0 h-1.5 cursor-row-resize z-[70] group"
        onMouseDown={handleResizeMouseDown}
      >
        <div className="w-full h-full bg-transparent group-hover:bg-[#30A84B]/20 transition-colors" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-1 bg-gray-200 rounded-full group-hover:bg-[#30A84B]/40 transition-colors" />
      </div>
      {/* Modern Header */}
      <div className="h-[56px] border-b border-gray-50 flex items-center px-6 justify-between bg-white shrink-0">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <button
              onClick={onTogglePlay}
              className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all ${isPlaying ? 'bg-[#30A84B] text-white shadow-[0_4px_12px_rgba(48,168,75,0.25)]' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              title={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" className="ml-1" />}
            </button>
            <div className="flex flex-col ml-1">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Time</span>
              <span className="font-mono text-[15px] font-bold text-gray-900 tabular-nums">
                {(currentTime / 1000).toFixed(2)}<span className="text-gray-400 ml-0.5">s</span>
              </span>
            </div>
          </div>

          <div className="h-8 w-px bg-gray-100" />

          {/* Duration Adjustment */}
          <div className="flex items-center gap-3">
            <div className="flex flex-col">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Total Duration</span>
                <div className="flex items-center gap-2">
                  <Clock size={12} className="text-gray-400 mr-1.5" />
                  <div className="flex items-center">
                    <span className="text-[13px] font-bold text-gray-700 tabular-nums">
                      {Math.round((templateSettings.duration || 20000) / 1000)}
                    </span>
                    <span className="text-[11px] font-bold text-gray-400 ml-0.5">s</span>
                  </div>
                </div>

                {/* Min/Max indicators */}
                <div className="flex flex-col">
                  {templateData && (
                    <span className="text-[8px] font-black text-gray-300 uppercase leading-tight">
                      Min: {Math.round(calculateMinTemplateDuration(templateData) / 1000)}s
                    </span>
                  )}
                  <span className="text-[8px] font-black text-gray-300 uppercase leading-tight text-red-400">Max: 30s</span>
                </div>
              </div>
            </div>
          </div>

          <div className="h-8 w-px bg-gray-100" />

          <div className="flex items-center gap-4">
            <div className="flex items-center bg-gray-50 rounded-xl p-1">
              <button
                onClick={() => setZoom(z => Math.max(10, z * 0.8))}
                className="p-1.5 hover:bg-white hover:shadow-sm rounded-lg text-gray-500 transition-all"
                title="Zoom Out"
              >
                <ZoomOut size={16} />
              </button>
              <span className="text-[11px] font-black text-gray-400 w-[44px] text-center uppercase tracking-tighter">
                {Math.round(zoom)}%
              </span>
              <button
                onClick={() => setZoom(z => Math.min(500, z * 1.2))}
                className="p-1.5 hover:bg-white hover:shadow-sm rounded-lg text-gray-500 transition-all"
                title="Zoom In"
              >
                <ZoomIn size={16} />
              </button>
            </div>

            <button
              onClick={() => setIsAdvancedVisible(!isAdvancedVisible)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-[12px] font-bold transition-all ${isAdvancedVisible ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'
                }`}
            >
              <Settings2 size={14} />
              <span>Advanced</span>
            </button>
          </div>

        <button onClick={onClose} className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-all">
          <X size={20} />
        </button>
      </div>

      {/* Content Split: Sidebar | Timeline Area */}
      <div className="flex-1 flex overflow-hidden bg-white">
        {/* Refined Sidebar */}
        <div className="w-[280px] border-r border-gray-50 flex-shrink-0 bg-white flex flex-col z-20">
          <div className="h-10 flex items-center px-6 bg-white border-b border-gray-50 shrink-0">
            <span className="text-[10px] font-black text-gray-300 uppercase tracking-[0.2em]">Layer Tracks</span>
          </div>
          <div className="overflow-y-auto overflow-x-hidden scrollbar-none flex-1 pb-10">
            {tracks.map((track, index) => {
              const isSelected = selectedLayerIds.includes(track.id);
              return (
                <React.Fragment key={`sidebar-track-${track.id}-${index}`}>
                  <div
                    className={`h-[52px] flex items-center px-4 transition-all border-b border-gray-50/50 group cursor-pointer ${isSelected ? 'bg-[#30A84B]/[0.03]' : 'hover:bg-gray-50/50'
                      }`}
                    onClick={() => onSelectLayer(track.id)}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div
                        className={`w-4 h-4 flex items-center justify-center cursor-pointer hover:bg-gray-100 rounded transition-transform ${expandedTracks.has(track.id) ? 'rotate-90' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTrack(track.id);
                        }}
                      >
                        <ChevronRight size={12} className="text-gray-400" />
                      </div>
                      <div className={`w-1.5 h-1.5 rounded-full shrink-0 transition-transform ${isSelected ? 'bg-[#30A84B] scale-125' : 'bg-gray-200 group-hover:bg-gray-300'}`} />
                      <div className="flex flex-col min-w-0">
                        <span className={`truncate text-[13px] font-bold tracking-tight ${isSelected ? 'text-[#30A84B]' : 'text-gray-700'}`}>
                          {track.name}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{track.type}</span>
                          {isAdvancedVisible && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddKeyframe(track);
                              }}
                              className="bg-[#30A84B]/10 hover:bg-[#30A84B]/20 text-[#30A84B] px-1 rounded text-[8px] font-bold transition-colors ml-1"
                            >
                              + KEY
                            </button>
                          )}
                          {isAdvancedVisible && (
                            <div className="flex items-center gap-1">
                              <div className="w-1 h-1 rounded-full bg-gray-200" />
                              <span className="text-[9px] font-bold text-gray-400 uppercase">{track.animation.easing}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {isSelected && <ChevronRight size={14} className="text-[#30A84B] opacity-50" />}
                  </div>
                  {expandedTracks.has(track.id) && (
                    <div className="bg-gray-50/30 flex flex-col">
                      {['Opacity', 'Transform', 'Filter'].map((prop) => (
                        <div key={`${track.id}-${prop}`} className="h-8 flex items-center px-12 border-b border-gray-50/50">
                          <span className="text-[10px] font-medium text-gray-400">{prop}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Timeline Grid Area */}
        <div className="flex-1 overflow-x-auto overflow-y-hidden relative bg-[#FDFDFD]" ref={containerRef}>
          {/* Ruler & Grid Overlay */}
          <div className="min-w-full h-full relative" style={{ width: `${timeToPx(templateSettings.duration || 20000) + 200}px` }}>
            {/* The "Curtain" - Dimming area after duration */}
            <div 
              className="absolute top-0 bottom-0 right-0 bg-gray-900/[0.03] backdrop-blur-[0.5px] pointer-events-none z-[5]"
              style={{ left: timeToPx(templateSettings.duration || 20000) }}
            >
              <div className="absolute top-1/2 left-4 -translate-y-1/2 flex flex-col gap-1 opacity-20">
                <div className="w-12 h-0.5 bg-gray-400" />
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">End of Template</span>
                <div className="w-12 h-0.5 bg-gray-400" />
              </div>
            </div>

            {/* Refined Ruler */}
            <div
              className="h-10 border-b border-gray-50 sticky top-0 bg-white z-30 cursor-pointer"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left + (containerRef.current?.scrollLeft || 0);
                const newTime = Math.max(0, pxToTime(x));
                console.log('[ANIM_DEBUG] Ruler Clicked:', newTime);
                onTimeChange(newTime);
              }}
            >
              {Array.from({ length: Math.ceil((templateSettings.duration || 20000) / 1000) + 1 }).map((_, i) => (
                <div
                  key={`ruler-mark-${i}`}
                  className="absolute bottom-0 h-3 border-l border-gray-200 text-[10px] font-bold text-gray-300 pl-1.5 pointer-events-none pb-0.5"
                  style={{ left: timeToPx(i * 1000) }}
                >
                  {i}s
                </div>
              ))}

              {/* Minimalist Playhead Handle */}
              <div
                className="absolute top-0 bottom-0 w-[14px] -ml-[7px] cursor-ew-resize z-40 group"
                style={{ left: timeToPx(currentTime) }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  const startX = e.clientX;
                  const startT = currentTime;
                  const handleMove = (ev: MouseEvent) => {
                    const dx = ev.clientX - startX;
                    const dt = pxToTime(dx);
                    const newTime = Math.max(0, startT + dt);
                    console.log('[ANIM_DEBUG] Playhead Dragging:', newTime);
                    onTimeChange(newTime);
                  };
                  const handleUp = () => {
                    window.removeEventListener('mousemove', handleMove);
                    window.removeEventListener('mouseup', handleUp);
                  };
                  window.addEventListener('mousemove', handleMove);
                  window.addEventListener('mouseup', handleUp);
                }}
              >
                <div className="w-[2px] h-full bg-[#30A84B] mx-auto relative">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3.5 h-3.5 bg-[#30A84B] rounded-[4px] shadow-[0_4px_10px_rgba(48,168,75,0.4)] flex items-center justify-center">
                    <div className="w-1 h-1 bg-white rounded-full" />
                  </div>
                </div>
              </div>

              {/* Draggable Duration End Handle */}
              <div
                className="absolute top-0 bottom-0 w-[30px] -mr-[15px] cursor-ew-resize z-40 group/duration-handle"
                style={{ left: timeToPx(templateSettings.duration || 20000) }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  const startX = e.clientX;
                  const startDur = templateSettings.duration || 20000;
                  const handleMove = (ev: MouseEvent) => {
                    const dx = ev.clientX - startX;
                    const dt = pxToTime(dx);
                    // Force Max 30s limit
                    const newDur = Math.min(30000, Math.max(2000, startDur + dt));
                    
                    const minMs = templateData ? calculateMinTemplateDuration(templateData) : 2000;
                    const finalDur = Math.max(minMs, newDur);
                    
                    // Snap to 0.5s intervals for smoother dragging
                    const snappedDur = Math.round(finalDur / 500) * 500;
                    onUpdateTemplateSettings({ duration: snappedDur });
                  };
                  const handleUp = () => {
                    window.removeEventListener('mouseup', handleUp);
                    window.removeEventListener('mousemove', handleMove);
                  };
                  window.addEventListener('mousemove', handleMove);
                  window.addEventListener('mouseup', handleUp);
                }}
              >
                <div className="w-[6px] h-full bg-red-500/80 group-hover/duration-handle:bg-red-500 transition-colors mx-auto relative flex items-center justify-center">
                   <div className="absolute top-0 px-2 py-1 bg-red-500 text-white text-[9px] font-black rounded-b shadow-[0_4px_10px_rgba(239,68,68,0.3)] whitespace-nowrap z-50">
                      DURATION
                   </div>
                   <div className="flex flex-col gap-1 opacity-40">
                      <div className="w-1 h-1 bg-white rounded-full" />
                      <div className="w-1 h-1 bg-white rounded-full" />
                      <div className="w-1 h-1 bg-white rounded-full" />
                   </div>
                </div>
              </div>
            </div>

            {/* Tracks Content Area */}
            <div
              className="overflow-y-auto overflow-x-hidden scrollbar-none h-[calc(100%-40px)]"
              onScroll={(e) => {
                const sidebar = e.currentTarget.parentElement?.parentElement?.previousElementSibling?.children[1];
                if (sidebar) sidebar.scrollTop = e.currentTarget.scrollTop;
              }}
            >
              {tracks.map((track, index) => {
                const delay = track.animation.delay || 0;
                const duration = track.animation.duration || 1000;
                const isSelected = selectedLayerIds.includes(track.id);

                return (
                  <div
                    key={`timeline-row-${track.id}-${index}`}
                    className={`h-[52px] border-b border-gray-50/50 relative group/row transition-colors ${isSelected ? 'bg-[#30A84B]/[0.01]' : ''}`}
                  >
                    {/* Visual Grid Lines (Vertical) */}
                    {Array.from({ length: Math.ceil((templateSettings.duration || 20000) / 1000) + 1 }).map((_, i) => (
                      <div
                        key={`grid-line-${track.id}-${i}`}
                        className="absolute top-0 bottom-0 border-l border-gray-50 pointer-events-none"
                        style={{ left: timeToPx(i * 1000) }}
                      />
                    ))}

                    {/* Refined Animation Bar */}
                    <div
                      className={`absolute top-3 bottom-3 rounded-full cursor-grab active:cursor-grabbing group/bar transition-all flex items-center overflow-hidden border ${isSelected
                        ? 'bg-[#30A84B] border-[#30A84B] shadow-[0_4px_12px_rgba(48,168,75,0.15)] ring-2 ring-[#30A84B]/10 ring-offset-2'
                        : 'bg-[#30A84B]/40 border-[#30A84B]/10 hover:bg-[#30A84B]/60'
                        }`}
                      style={{ left: timeToPx(delay), width: Math.max(20, timeToPx(duration)) }}
                      onMouseDown={(e) => handleMouseDown(e, track, 'move')}
                    >
                      {/* Enhanced Resize Handles */}
                      <div
                        className="absolute left-0 top-0 bottom-0 w-4 cursor-w-resize opacity-0 group-hover/bar:opacity-100 hover:bg-white/20 transition-all z-10 flex items-center justify-center"
                        onMouseDown={(e) => handleMouseDown(e, track, 'resize-start')}
                      >
                        <div className="w-1 h-4 bg-white/40 rounded-full" />
                      </div>
                      <div
                        className="absolute right-0 top-0 bottom-0 w-4 cursor-e-resize opacity-0 group-hover/bar:opacity-100 hover:bg-white/20 transition-all z-10 flex items-center justify-center"
                        onMouseDown={(e) => handleMouseDown(e, track, 'resize-end')}
                      >
                        <div className="w-1 h-4 bg-white/40 rounded-full" />
                      </div>

                      <span className="text-[10px] text-white font-black uppercase tracking-widest pl-5 pr-5 select-none truncate opacity-90 drop-shadow-sm">
                        {track.animation.type}
                      </span>

                      {/* Progressive Disclosure: Only show keyframes if advanced mode is on */}
                      {isAdvancedVisible && track.animation.keyframes?.map((kf, kfIdx) => (
                        <div
                          key={`kf-${track.id}-${kfIdx}`}
                          className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-white rounded-full shadow-sm pointer-events-none ring-2 ring-black/5"
                          style={{ left: `${(kf.time / duration) * 100}%`, marginLeft: '-3px' }}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
              <div className="h-20" />
            </div>

            {/* High-Contrast Playhead Line */}
            <div
              className="absolute top-10 bottom-0 w-[1.5px] bg-[#30A84B] pointer-events-none z-10 opacity-40 shadow-[0_0_8px_rgba(48,168,75,0.4)]"
              style={{ left: timeToPx(currentTime) }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

